/**
 * Rate Master v2.0 — generic API client factory.
 *
 * All 8 CRUD entities (OEM prices, labour rates, labour time, workshop
 * factors, repair matrix, paint matrix, quality factors, city tiers,
 * adjustments) share the exact same route shape:
 *   GET    {base}?...filters&limit&offset  -> ListResponse<T>
 *   GET    {base}/{id}                     -> T
 *   POST   {base}/import (multipart)       -> ImportResponse
 *   PUT    {base}/{id}                     -> T
 *   HTTP_DELETE {base}/{id}                -> 204 (soft delete)
 *
 * createEntityClient() builds one typed client per entity instead of
 * hand-writing 8 near-identical fetch modules. Mirrors the existing
 * convention in lib/rate-master/api.ts (localStorage token lookup,
 * {detail} error parsing).
 *
 * Uses NEXT_PUBLIC_RATE_MASTER_API_URL (falling back to
 * NEXT_PUBLIC_API_URL, then localhost:8000) as its own dedicated base
 * URL — deliberately NOT shared with lib/api-client.ts's NEXT_PUBLIC_API_URL,
 * since that shared client is also used for auth/login and every other
 * feature. Rate Master v2 in local dev often needs to point at a
 * different insurance-service instance than the one Kong (port 8000)
 * routes everything else to, and overriding NEXT_PUBLIC_API_URL
 * globally would silently break login and other features that read the
 * same variable.
 */

import type { ImportResponse, ListResponse } from '@/types/rate-master-v2';

const HTTP_GET = 'GET';
const HTTP_PUT = 'PUT';
const HTTP_POST = 'POST';
const HTTP_DELETE = 'DELETE';

const API_URL =
  process.env.NEXT_PUBLIC_RATE_MASTER_API_URL ||
  process.env.NEXT_PUBLIC_API_URL ||
  'http://localhost:8000';
const RATE_MASTER_BASE = `${API_URL}/api/v1/insurance/rate-master`;

function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  return (
    localStorage.getItem('super_admin_token') || localStorage.getItem('access_token')
  );
}

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

function authHeadersForUpload(): Record<string, string> {
  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function parseErrorMessage(response: Response): Promise<string> {
  const defaultMessage = `Request failed with status ${response.status}`;
  try {
    const body = await response.json();
    if (!body?.detail) {
      return defaultMessage;
    }
    return typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
  } catch {
    return defaultMessage;
  }
}

async function assertOk(response: Response): Promise<void> {
  if (response.ok) {
    return;
  }
  const message = await parseErrorMessage(response);
  throw new Error(message);
}

async function fetchJson<T>(response: Response): Promise<T> {
  await assertOk(response);
  const data = (await response.json()) as T;
  return data;
}

export interface EntityClient<TRecord, TUpdatePayload, TCreatePayload = never> {
  list: (
    params?: Record<string, string | number | boolean | undefined>,
  ) => Promise<ListResponse<TRecord>>;
  get: (id: string) => Promise<TRecord>;
  create: (payload: TCreatePayload) => Promise<TRecord>;
  update: (id: string, payload: TUpdatePayload) => Promise<TRecord>;
  remove: (id: string) => Promise<void>;
  importFile: (file: File) => Promise<ImportResponse>;
}

function buildQueryString(params: Record<string, string | number | boolean | undefined>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      qs.set(key, String(value));
    }
  }
  return qs.toString();
}

function entityUrl(base: string, id: string): string {
  return [base, id].join('/');
}

/**
 * @param path route segment relative to /api/v1/insurance/rate-master,
 *   e.g. "oem-prices" or "labour-rates" — no leading/trailing slash.
 */
export function createEntityClient<TRecord, TUpdatePayload, TCreatePayload = never>(
  path: string,
): EntityClient<TRecord, TUpdatePayload, TCreatePayload> {
  const base = `${RATE_MASTER_BASE}/${path}`;

  async function list(
    params: Record<string, string | number | boolean | undefined> = {},
  ): Promise<ListResponse<TRecord>> {
    const query = buildQueryString(params);
    const url = query ? `${base}?${query}` : base;
    const response = await fetch(url, { method: HTTP_GET, headers: authHeaders() });
    return fetchJson<ListResponse<TRecord>>(response);
  }

  async function get(id: string): Promise<TRecord> {
    const response = await fetch(entityUrl(base, id), { method: HTTP_GET, headers: authHeaders() });
    return fetchJson<TRecord>(response);
  }

  async function create(payload: TCreatePayload): Promise<TRecord> {
    const response = await fetch(base, {
      method: HTTP_POST,
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    return fetchJson<TRecord>(response);
  }

  async function update(id: string, payload: TUpdatePayload): Promise<TRecord> {
    const response = await fetch(entityUrl(base, id), {
      method: HTTP_PUT,
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });
    return fetchJson<TRecord>(response);
  }

  async function remove(id: string): Promise<void> {
    const response = await fetch(entityUrl(base, id), {
      method: HTTP_DELETE,
      headers: authHeaders(),
    });
    await assertOk(response);
  }

  async function importFile(file: File): Promise<ImportResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${base}/import`, {
      method: HTTP_POST,
      headers: authHeadersForUpload(),
      body: formData,
    });
    return fetchJson<ImportResponse>(response);
  }

  return { list, get, create, update, remove, importFile };
}

export { RATE_MASTER_BASE, authHeaders, fetchJson, assertOk };
